import { FC } from "react";

import { AttachmentChip } from "./AttachmentChip";
import { BotAvatar } from "./BotAvatar";
import { SuggestionChip } from "./SuggestionChip";
import { AgentChatAttachment, AgentChatMessage, ChatbotClassMap, ChatbotCx } from "../types";

interface Props {
  readonly styles: ChatbotClassMap;
  readonly cx: ChatbotCx;
  readonly messages: readonly AgentChatMessage[];
  readonly onSuggestionSelect?: (suggestion: string) => void;
}

const renderAttachments = (styles: ChatbotClassMap, attachments: readonly AgentChatAttachment[]) => (
  <div className={styles.DiscussionMessageAttachments}>
    {attachments.map((file) => (
      <AttachmentChip key={file.id} styles={styles} file={file} />
    ))}
  </div>
);

/* Single definition of "nothing to put in a message bubble", shared by the row-level skip below so
   the two checks cannot drift apart. */
const hasEmptyContent = (message: AgentChatMessage): boolean => message.content === "" || message.content == null;

/* An assistant turn is inserted the moment a reply is requested, before any text arrives. Such a
   turn has nothing to show, so the whole row is skipped rather than leaving a byline stranded above
   empty space; the thinking indicator carries the avatar until the first chunk lands. */
export const isEmptyAssistantTurn = (message: AgentChatMessage): boolean => {
  if (message.role !== "assistant") return false;
  if ((message.attachments ?? []).length > 0) return false;
  if ((message.suggestions ?? []).length > 0) return false;
  return hasEmptyContent(message);
};

/** Scrolling conversation view rendering user and assistant turns with attachments and suggestion chips. */
export const DiscussionView: FC<Props> = ({ styles, cx, messages, onSuggestionSelect }) => (
  <ul className={styles.DiscussionMessages} aria-live="polite" aria-relevant="additions text">
    {messages.map((message) => {
      if (isEmptyAssistantTurn(message)) return null;

      const isAssistant = message.role === "assistant";
      const attachments = message.attachments ?? [];
      const hasAttached = attachments?.length > 0;
      const suggestions = isAssistant ? (message.suggestions ?? []) : [];
      /* Fully empty turns are already gone, so this only catches a suggestions-only reply: render
         its chips without an empty bubble above them. */
      const isEmptyPlaceholder = isAssistant && !hasAttached && hasEmptyContent(message);
      return (
        <li
          key={message.id}
          className={cx(styles.DiscussionMessageRow, isAssistant ? styles.DiscussionAssistantRow : styles.DiscussionUserRow)}
        >
          {!!isAssistant && <BotAvatar styles={styles} />}
          {!isEmptyPlaceholder && (
            <div className={cx(styles.DiscussionMessage, isAssistant ? styles.DiscussionAssistantMessage : styles.DiscussionUserMessage)} {...(hasAttached ? { "data-has-attachments": true } : {})}>
              {hasAttached && renderAttachments(styles, attachments)}
              <>{message.content}</>
            </div>
          )}
          {suggestions.length > 0 && (
            <div className={styles.DiscussionSuggestions}>
              {suggestions.map((suggestion, index) => (
                <SuggestionChip
                  key={`${suggestion}::${index}`}
                  styles={styles}
                  label={suggestion}
                  onSelect={(value) => onSuggestionSelect?.(value)}
                />
              ))}
            </div>
          )}
        </li>
      );
    })}
  </ul>
);
